import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Link, useNavigate } from 'react-router-dom';
import {
  cancelCheckout,
  cancelOrder,
  getCheckoutStatus,
  getOrder,
  getOrderPaymentUrl,
  getPublicMenu,
  submitPaymentProof,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { OrderSuccess, OrderTracker } from '../components/pizzeria/OrderCelebration';
import LegalFooterBlock from '../components/legal/LegalFooterBlock';
import { CURRENT_NOTICE_VERSION, legalValue, useLegalIdentity, waHref } from '../lib/legalIdentity';

const PIZAS_MODE = process.env.REACT_APP_PIZAS_MODE === '1';

export default function PayOrderPage() {
  const { orderId, checkoutId } = useParams();
  const isCheckout = Boolean(checkoutId);
  const id = checkoutId || orderId;
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [payUrl, setPayUrl] = useState('');
  const [transferInstructions, setTransferInstructions] = useState('');
  const [reference, setReference] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [submittingProof, setSubmittingProof] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  // Paquete legal v2026-10-09
  const [proofConsent, setProofConsent] = useState(false);
  const [mpSandbox, setMpSandbox] = useState(true);
  const { identity } = useLegalIdentity();
  const whatsapp = legalValue(identity, 'whatsapp');
  const tradeName = legalValue(identity, 'trade_name') || 'DanySolutions';
  const navigate = useNavigate();

  const loadCheckout = async () => {
    const ch = await getCheckoutStatus(id);
    setCheckout(ch);
    setPayUrl(ch.init_point || '');
    if (ch.order_id) {
      const o = await getOrder(ch.order_id);
      setOrder(o);
      sessionStorage.removeItem('pizas_cart');
      sessionStorage.removeItem('pizas_cart_pending_checkout');
    }
    return ch;
  };

  const loadOrder = async () => {
    const o = await getOrder(id);
    setOrder(o);
    return o;
  };

  useEffect(() => {
    if (!id) return;
    const init = async () => {
      try {
        const menu = await getPublicMenu();
        setTransferInstructions(menu?.store?.transfer_bank_instructions || '');
        setMpSandbox(menu?.store?.mercadopago_sandbox !== false);
        if (isCheckout) {
          await loadCheckout();
        } else {
          const o = await getOrder(id);
          setOrder(o);
          const p = await getOrderPaymentUrl(id);
          setPayUrl(p.payment_url || o.mercadopago_init_point || '');
        }
      } catch {
        toast.error('No se encontró el pedido o el pago');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id, isCheckout]);

  useEffect(() => {
    if (!isCheckout || !id) return;
    if (searchParams.get('status') !== 'ok' && checkout?.status !== 'completed') return;
    let tries = 0;
    const maxTries = 24;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const ch = await loadCheckout();
        if (ch.order_id || ch.status === 'completed' || tries >= maxTries) {
          clearInterval(timer);
        }
      } catch {
        clearInterval(timer);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [id, isCheckout, searchParams, checkout?.status]);

  const refreshPayUrl = async () => {
    try {
      if (isCheckout) {
        const ch = await getCheckoutStatus(id);
        setPayUrl(ch.init_point || '');
      } else {
        const p = await getOrderPaymentUrl(id);
        setPayUrl(p.payment_url || '');
      }
    } catch {
      toast.error('No se pudo obtener el link de pago');
    }
  };

  const clearCartAndGoMenu = () => {
    sessionStorage.removeItem('pizas_cart');
    sessionStorage.removeItem('pizas_cart_pending_checkout');
    navigate('/pedir');
  };

  // Cancelación pública solo antes de la preparación (términos §7). El backend responde 409 si ya no se puede.
  const handleCancel = async () => {
    const paid = order?.payment_status === 'paid';
    const msg = paid
      ? '¿Cancelar este pedido? Le devolveremos el importe completo por el mismo medio de pago.'
      : '¿Cancelar este pedido? No se cobrará nada.';
    if (!window.confirm(msg)) return;
    try {
      setCancelling(true);
      if (isCheckout && !order) {
        await cancelCheckout(id);
      } else {
        await cancelOrder(order?.id || id);
      }
      toast.success(paid ? 'Pedido cancelado. Tramitaremos su reembolso.' : 'Pedido cancelado');
      clearCartAndGoMenu();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo cancelar');
      if (err?.response?.status === 409 && order?.id) {
        try {
          setOrder(await getOrder(order.id));
        } catch {
          /* nada */
        }
      }
    } finally {
      setCancelling(false);
    }
  };

  const paymentStatusLabel = (ps) => {
    if (ps === 'paid') return 'Pagado';
    if (ps === 'awaiting_receipt' || ps === 'unpaid') return 'Pendiente comprobante';
    if (ps === 'pending_verification') return 'Comprobante enviado — en revisión';
    if (ps === 'pay_on_delivery') return 'Paga al entregar';
    return ps;
  };

  const sendProof = async () => {
    const ref = reference.trim();
    if (!proofFile && ref.length < 3) {
      toast.error('Sube una captura o escribe al menos 3 caracteres de referencia');
      return;
    }
    if (!proofConsent) {
      toast.error('Para enviar el comprobante, marque la casilla de consentimiento.');
      document.getElementById('proof-consent')?.focus();
      return;
    }
    try {
      setSubmittingProof(true);
      await submitPaymentProof(order.id, {
        payment_reference: ref,
        file: proofFile,
        transfer_proof_consent: proofConsent,
        privacy_notice_version: legalValue(identity, 'notice_version') || CURRENT_NOTICE_VERSION,
      });
      toast.success('Comprobante enviado; el local verificará tu transferencia.');
      setProofFile(null);
      const o = await getOrder(order.id);
      setOrder(o);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo enviar el comprobante');
    } finally {
      setSubmittingProof(false);
    }
  };

  if (loading) return <div className="p-10 text-center">Cargando…</div>;

  if (isCheckout && !order && checkout?.status !== 'completed') {
    return (
      <div className="container mx-auto px-4 py-10 max-w-lg">
        <Card>
          <CardHeader>
            <CardTitle>Completa tu pago</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-2xl font-bold">${Number(checkout?.total || 0).toFixed(2)} MXN</p>
            <p className="text-sm text-muted-foreground">
              {checkout?.order_number ? (
                <>
                  Tu pedido <strong>#{checkout.order_number}</strong> ya está registrado con estado{' '}
                  <strong>pago pendiente</strong>. Cocina lo verá cuando Mercado Pago confirme el cobro.
                </>
              ) : (
                <>
                  Tu pedido <strong>aún no está en cocina</strong> hasta que Mercado Pago confirme el pago.
                </>
              )}
            </p>
            {mpSandbox ? (
              <p className="text-sm font-semibold" data-testid="mp-sandbox-notice">
                Mercado Pago en modo prueba: no se realizan cargos reales.
              </p>
            ) : null}
            {payUrl ? (
              <>
                <Button asChild className="w-full">
                  <a href={payUrl} target="_blank" rel="noreferrer">
                    Abrir Mercado Pago
                  </a>
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={refreshPayUrl}>
                  Actualizar link
                </Button>
              </>
            ) : (
              <p className="text-amber-700 text-sm">
                No hay link de pago. El administrador debe configurar Mercado Pago en Config tienda.
              </p>
            )}
            {searchParams.get('status') === 'ok' ? (
              <p className="text-sm animate-pulse">Verificando pago con el banco…</p>
            ) : null}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={cancelling || searchParams.get('status') === 'ok'}
              onClick={handleCancel}
            >
              {cancelling ? 'Cancelando…' : 'Cancelar pedido'}
            </Button>
            <Button asChild variant="ghost" size="sm">
              <Link to="/pedir">Volver al menú</Link>
            </Button>
          </CardContent>
        </Card>
        <LegalFooterBlock showCookiePrefs={false} className="mt-8 text-[#6B5444] [&_a]:text-[#C2410C]" />
      </div>
    );
  }

  if (!order) return <div className="p-10 text-center">Pedido no encontrado</div>;

  const isPaid = order.payment_status === 'paid';
  const isCod = order.payment_method === 'cash_on_delivery';
  const isCodPending =
    isCod && ['pending_review', 'proposed_delivery', 'pending_payment'].includes(order.status);

  const showCelebration =
    PIZAS_MODE && (isPaid || isCod) && !['cancelled', 'rejected'].includes(order.status);
  const canCancel = order.can_cancel !== false && !['cancelled', 'rejected', 'delivered'].includes(order.status);
  const finished = ['cancelled', 'rejected', 'delivered'].includes(order.status);
  const waLink = whatsapp ? waHref(whatsapp) : '';
  const cancelButton = canCancel ? (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      disabled={cancelling}
      onClick={handleCancel}
      data-testid="cancel-order-btn"
    >
      {cancelling ? 'Cancelando…' : 'Cancelar pedido'}
    </Button>
  ) : null;

  return (
    <div className={PIZAS_MODE ? 'trattoria t-paper min-h-screen' : ''}>
    <div className="container mx-auto px-4 py-10 max-w-lg">
      <Card className={PIZAS_MODE ? 't-card shadow-xl' : ''}>
        <CardHeader>
          <CardTitle className={PIZAS_MODE ? 'font-heading text-2xl font-extrabold' : ''}>
            {isCod ? `Pedido #${order.order_number}` : `Pago — Pedido #${order.order_number}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {showCelebration ? (
            <>
              <OrderSuccess
                title={isPaid ? '¡Pago recibido!' : '¡Pedido recibido!'}
                subtitle={isPaid ? 'Ya está en el sistema.' : 'Te confirmaremos por WhatsApp.'}
              />
              <OrderTracker status={order.status} deliveryType={order.delivery_type} />
            </>
          ) : null}
          <p className="text-2xl font-bold">${Number(order.total).toFixed(2)} MXN</p>
          {!finished ? (
            <p className="text-sm text-muted-foreground" data-testid="order-receipt-note">
              Pedido #{order.order_number} recibido. Le avisaremos por WhatsApp cuando lo aceptemos. Este es su
              comprobante: guárdelo.
              {whatsapp ? (
                <>
                  {' '}¿Algo salió mal con su pedido? Escríbanos al{' '}
                  <a href={waLink} target="_blank" rel="noreferrer" className="underline">
                    {whatsapp}
                  </a>
                  .
                </>
              ) : null}
            </p>
          ) : null}
          <Badge variant={isPaid ? 'default' : isCod ? 'outline' : 'secondary'}>
            {isPaid
              ? 'Pagado'
              : isCod
                ? 'Contra entrega'
                : paymentStatusLabel(order.payment_status)}
          </Badge>
          {isCodPending ? (
            <p className="text-sm text-muted-foreground">
              Tu pedido está en revisión. El local te escribirá por WhatsApp al{' '}
              <strong>{order.customer_phone}</strong> cuando lo acepten. Pagas al{' '}
              {order.delivery_type === 'pickup' ? 'recoger' : 'recibir'} el pedido.
            </p>
          ) : null}
          {isPaid ? (
            <p className="text-sm text-green-800">
              Gracias. Tu pedido ya está en el sistema y el equipo lo revisará en breve.
            </p>
          ) : null}
          {order.within_delivery_radius === false && (
            <p className="text-amber-700 text-sm">
              Tu dirección está a {order.distance_km} km (revisión manual del administrador).
            </p>
          )}
          {isCod || isPaid ? cancelButton : null}
          {!isPaid && !isCod && order.payment_method === 'mercadopago' && (
            <>
              <p className="text-xs text-muted-foreground">
                🔒 Pago seguro con Mercado Pago. Será redirigido a su sitio para pagar; no recibimos ni guardamos los
                datos de su tarjeta.
                {mpSandbox ? <strong> Modo prueba: no se realizan cargos reales.</strong> : null}
              </p>
              <Button asChild className="w-full">
                <a href={payUrl} target="_blank" rel="noreferrer">
                  Pagar con Mercado Pago
                </a>
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={refreshPayUrl}>
                Actualizar link de pago
              </Button>
              {cancelButton}
            </>
          )}
          {!isPaid && !isCod && order.payment_method === 'transfer' && (
            <>
              {order.payment_status === 'pending_verification' ? (
                <p className="text-sm text-muted-foreground">
                  Ya recibimos tu comprobante. El equipo lo revisará y te avisará por WhatsApp.
                </p>
              ) : null}
              {transferInstructions ? (
                <div className="text-sm whitespace-pre-line rounded-md border p-3 bg-muted/40">
                  {transferInstructions}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Realiza tu transferencia y sube la captura o indica la referencia.
                </p>
              )}
              {order.payment_status !== 'pending_verification' ? (
                <>
                  <div>
                    <Label htmlFor="proof">Captura de transferencia (JPG o PNG)</Label>
                    <Input
                      id="proof"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="ref">Referencia / últimos 4 dígitos (opcional si subes foto)</Label>
                    <Input id="ref" value={reference} onChange={(e) => setReference(e.target.value)} />
                  </div>
                  {/* Paquete legal (doc. 02 §C): consentimiento expreso para datos patrimoniales */}
                  <label className="flex items-start gap-2 text-sm cursor-pointer" htmlFor="proof-consent">
                    <input
                      id="proof-consent"
                      type="checkbox"
                      className="mt-1 h-4 w-4 shrink-0 accent-[#C2410C]"
                      checked={proofConsent}
                      onChange={(e) => setProofConsent(e.target.checked)}
                      data-testid="proof-consent"
                    />
                    <span>
                      Consiento expresamente que {tradeName} trate los datos de mi comprobante de transferencia
                      (titular, banco, parte de la cuenta, monto y clave de rastreo) solo para verificar el pago de este
                      pedido, conforme al{' '}
                      <Link to="/aviso-de-privacidad" target="_blank" className="underline">
                        Aviso de privacidad
                      </Link>
                      .
                    </span>
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Si no desea enviar la captura, puede escribir solo la clave de rastreo o la referencia.
                  </p>
                  <Button
                    onClick={sendProof}
                    disabled={submittingProof || !proofConsent || (!proofFile && reference.trim().length < 3)}
                  >
                    {submittingProof ? 'Enviando…' : 'Enviar comprobante'}
                  </Button>
                </>
              ) : null}
              {cancelButton}
            </>
          )}
          {!canCancel && !finished ? (
            <p className="text-sm text-muted-foreground" data-testid="cancel-blocked-note">
              Su pedido ya está en preparación y no se puede cancelar desde aquí.
              {whatsapp ? (
                <>
                  {' '}Si hay un problema, escríbanos por WhatsApp al{' '}
                  <a href={waLink} target="_blank" rel="noreferrer" className="underline">
                    {whatsapp}
                  </a>
                  .
                </>
              ) : null}
            </p>
          ) : null}
          {order.status === 'cancelled' && order.refund_status ? (
            <p className="text-sm text-muted-foreground" data-testid="refund-note">
              {order.refund_status === 'pending'
                ? 'Reembolso en trámite: le devolveremos el importe completo por el mismo medio de pago.'
                : 'Reembolso registrado.'}
            </p>
          ) : null}
          {!isPaid && order.status === 'cancelled' ? (
            <p className="text-sm text-muted-foreground">Este pedido fue cancelado.</p>
          ) : null}
          {!isPaid ? (
            <Button asChild variant="ghost" size="sm">
              <Link to="/pedir">Volver al menú</Link>
            </Button>
          ) : null}
        </CardContent>
      </Card>
      {/* Paquete legal: pie con enlaces legales (sin aviso de cookies ni analítica en /pagar) */}
      <LegalFooterBlock showCookiePrefs={false} className="mt-8 text-[#6B5444] [&_a]:text-[#C2410C]" />
    </div>
    </div>
  );
}
